<template>
    <teleport to="body">
        <div class="upm-overlay" @click.self="emit('close')">
            <section class="upm-dialog" role="dialog" aria-modal="true" aria-label="Thông tin tài khoản">
                <header class="upm-header">
                    <h2>Thông tin tài khoản</h2>
                    <button type="button" aria-label="Đóng" @click="emit('close')">×</button>
                </header>

                <template v-if="!loading">
                    <div class="upm-cover">
                        <span class="upm-cover-grid"></span>
                        <span class="upm-cover-orb"></span>
                    </div>

                    <div class="upm-identity">
                        <div class="upm-avatar-wrap">
                            <WasabiImage
                                v-if="profileImage"
                                :data="{ url: profileImage, title: displayName }"
                                :userImage="true"
                                thumbnail="160x160"
                                class="upm-avatar"
                            />
                            <div v-else class="upm-avatar upm-avatar-fallback">{{ initials }}</div>
                            <i :class="{ online: isOnline }"></i>
                        </div>
                        <div class="upm-name">
                            <h1>{{ displayName }}</h1>
                            <small v-if="nickname">Tên thật: {{ officialName }}</small>
                            <p>{{ designationLabel }}</p>
                        </div>
                        <button v-if="isOwnProfile" class="upm-edit" type="button" @click="editProfile">
                            Chỉnh sửa
                        </button>
                        <div v-else class="upm-actions">
                            <button class="upm-nickname-edit" type="button"
                                :title="nickname ? 'Sửa biệt danh' : 'Đặt biệt danh'"
                                :aria-label="nickname ? 'Sửa biệt danh' : 'Đặt biệt danh'"
                                @click="openNicknameEditor">
                                <img :src="editIcon" alt="">
                            </button>
                            <button class="upm-message" type="button" :disabled="openingChat" @click="openDirectChat">
                                {{ openingChat ? $t('Profile.opening_chat') : $t('Profile.message') }}
                            </button>
                        </div>
                    </div>

                    <NicknameDialog v-if="editingNickname" v-model="nicknameDraft" :user-id="userId"
                        :official-name="officialName" :avatar-url="profileImage" :nickname="nickname"
                        :saving="savingNickname" :error="nicknameError" @confirm="saveNickname"
                        @cancel="cancelNicknameEditor" @remove="removeNickname" />
                    <div class="upm-content">
                        <h3>Thông tin cá nhân</h3>
                        <dl>
                            <div><dt>Bio</dt><dd>{{ valueOrEmpty(user?.bio || user?.Bio) }}</dd></div>
                            <div><dt>Email</dt><dd>{{ valueOrEmpty(user?.Employee_Email) }}</dd></div>
                            <div><dt>Điện thoại</dt><dd>{{ valueOrEmpty(user?.Employee_Phone) }}</dd></div>
                            <div><dt>Giới tính</dt><dd>{{ genderLabel }}</dd></div>
                            <div><dt>Ngày sinh</dt><dd>{{ birthdayLabel }}</dd></div>
                        </dl>

                        <div class="upm-divider"></div>
                        <h3>Thông tin công việc</h3>
                        <dl>
                            <div><dt>Chức danh</dt><dd>{{ designationLabel }}</dd></div>
                            <div><dt>Vai trò</dt><dd>{{ roleLabel }}</dd></div>
                            <div><dt>Trạng thái</dt><dd><span class="upm-status">Đang hoạt động</span></dd></div>
                        </dl>
                    </div>
                </template>

                <div v-else class="upm-loading">
                    <div></div><span></span><span></span>
                </div>
            </section>
        </div>
    </teleport>
</template>

<script setup>
import { computed, defineEmits, defineProps, onMounted, onBeforeUnmount, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toast-notification';
import WasabiImage from '@/components/atom/WasabiIamgeCompp/WasabiIamgeCompp.vue';
import NicknameDialog from '@/components/organisms/NicknameDialog/NicknameDialog.vue';
import { apiRequest, apiRequestWithoutCompnay } from '@/services';
import * as env from '@/config/env';

const editIcon = require('@/assets/images/svg/edit_icon_black.svg');

const props = defineProps({ userId: { type: String, required: true } });
const emit = defineEmits(['close']);
const router = useRouter();
const store = useStore();
const { t } = useI18n();
const toast = useToast();
const fetchedUser = ref(null);
const loading = ref(true);
const editingNickname = ref(false);
const nicknameDraft = ref('');
const savingNickname = ref(false);
const nicknameError = ref('');
const openingChat = ref(false);

const users = computed(() => store.getters['users/users'] || []);
const companyUsers = computed(() => store.getters['settings/companyUsers'] || []);
const designations = computed(() => store.getters['settings/designations'] || []);
const user = computed(() => users.value.find((item) => String(item?._id || item?.id) === String(props.userId)) || fetchedUser.value);
const companyUser = computed(() => companyUsers.value.find((item) => String(item?.userId || item?._id) === String(props.userId)) || {});
const currentCompanyUser = computed(() => store.getters['settings/companyUserDetail'] || {});
const nickname = computed(() => String(currentCompanyUser.value?.nicknames?.[props.userId] || '').trim());
const officialName = computed(() => user.value?.Employee_Name || user.value?.name || 'Người dùng Kompact');
const displayName = computed(() => nickname.value || officialName.value);
const profileImage = computed(() => user.value?.Employee_profileImageURL || user.value?.Employee_profileImage || '');
const initials = computed(() => displayName.value.split(/\s+/).filter(Boolean).slice(-2).map((word) => word[0]).join('').toUpperCase());
const isOwnProfile = computed(() => String(localStorage.getItem('userId')) === String(props.userId));
const isOnline = computed(() => Boolean(user.value?.isOnline ?? companyUser.value?.isOnline));
const designationLabel = computed(() => {
    const key = companyUser.value?.designation ?? user.value?.designation;
    return designations.value.find((item) => String(item?.key) === String(key))?.name || valueOrEmpty(user.value?.designationName || companyUser.value?.designationName);
});
const roleLabel = computed(() => {
    const role = companyUser.value?.roleType;
    if (role === 1) return 'Chủ sở hữu';
    if (role === 2) return 'Quản trị viên';
    if (role === 3) return 'Thành viên';
    return valueOrEmpty(companyUser.value?.roleName || user.value?.roleName);
});
const genderLabel = computed(() => {
    const gender = user.value?.gender ?? user.value?.Gender;
    if (gender === 1 || String(gender).toLowerCase() === 'male') return 'Nam';
    if (gender === 2 || String(gender).toLowerCase() === 'female') return 'Nữ';
    return valueOrEmpty(gender);
});
const birthdayLabel = computed(() => {
    const value = user.value?.birthday || user.value?.dateOfBirth || user.value?.Birthday;
    if (!value) return 'Chưa cập nhật';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('vi-VN').format(date);
});

function valueOrEmpty(value) { return value || 'Chưa cập nhật'; }
function closeOnEscape(event) { if (event.key === 'Escape' && !editingNickname.value) emit('close'); }
function editProfile() {
    emit('close');
    router.push({ name: 'My Profile', params: { cid: localStorage.getItem('selectedCompany') } });
}
function openNicknameEditor() {
    nicknameDraft.value = nickname.value;
    nicknameError.value = '';
    editingNickname.value = true;
}
async function openDirectChat() {
    if (openingChat.value || isOwnProfile.value) return;
    openingChat.value = true;
    try {
        const companyId = router.currentRoute.value.params.cid || localStorage.getItem('selectedCompany');
        const currentUserId = localStorage.getItem('userId');
        const response = await apiRequest('post', env.CONVERSATIONS, {
            companyId,
            chatType: 'oneToOne',
            memberIds: [props.userId],
            createdBy: currentUserId,
        });
        const conversation = response?.data?.data;
        const chatId = conversation?._id || conversation?.id;
        if (!response?.data?.status || !chatId) throw new Error(response?.data?.message);

        store.commit('mainChat/mutateConversations', [{
            op: 'added',
            data: { ...conversation, id: chatId, _id: chatId, chatType: 'oneToOne' },
        }]);
        emit('close');
        await router.push({ name: 'chat_group', params: { cid: companyId, chatId } });
    } catch (error) {
        toast.error(error?.response?.data?.message || error?.message || t('generalErrorMessage.something_went_wrong'), { position: 'top-right' });
    } finally {
        openingChat.value = false;
    }
}
function cancelNicknameEditor() {
    editingNickname.value = false;
    nicknameError.value = '';
}
async function persistNickname(value) {
    if (savingNickname.value) return;
    savingNickname.value = true;
    nicknameError.value = '';
    try {
        const normalized = String(value || '').trim().replace(/\s+/g, ' ');
        await apiRequest('put', `${env.API_MEMBERS}/nickname`, {
            targetUserId: props.userId,
            nickname: normalized
        });
        store.commit('settings/mutateNickname', { targetUserId: props.userId, nickname: normalized });
        nicknameDraft.value = normalized;
        editingNickname.value = false;
    } catch (error) {
        nicknameError.value = error?.response?.data?.message || error?.message || 'Không thể lưu biệt danh.';
    } finally {
        savingNickname.value = false;
    }
}
function saveNickname() { return persistNickname(nicknameDraft.value); }
function removeNickname() { return persistNickname(''); }

onMounted(async () => {
    document.addEventListener('keydown', closeOnEscape);
    document.body.classList.add('upm-open');
    try {
        if (!user.value) {
            const response = await apiRequestWithoutCompnay('get', `${env.USER_UPATE}/${props.userId}`);
            fetchedUser.value = response?.data?.data || response?.data || null;
        }
    } catch (error) {
        console.error('Unable to load user profile:', error);
    } finally {
        loading.value = false;
    }
});

onBeforeUnmount(() => {
    document.removeEventListener('keydown', closeOnEscape);
    document.body.classList.remove('upm-open');
});
</script>

<style scoped>
.upm-overlay { position: fixed; inset: 0; z-index: 100000; display: grid; place-items: center; padding: 20px; background: rgba(19,30,48,.48); backdrop-filter: blur(2px); }
.upm-dialog { width: min(470px, calc(100vw - 24px)); max-height: calc(100vh - 40px); overflow: auto; border-radius: 16px; background: #fff; box-shadow: 0 24px 70px rgba(15,31,55,.3); color: #1e2a40; font-family: 'Inter', sans-serif; }
.upm-header { position: sticky; top: 0; z-index: 3; display: flex; align-items: center; justify-content: space-between; height: 56px; padding: 0 20px; border-bottom: 1px solid #e8edf4; background: #fff; }
.upm-header h2 { margin: 0; font-size: 17px; }
.upm-header button { border: 0; background: transparent; color: #66738a; font-size: 30px; line-height: 1; cursor: pointer; }
.upm-cover { position: relative; height: 178px; overflow: hidden; background: linear-gradient(130deg,#075fc9,#148ae9 54%,#79d1e7); }
.upm-cover-grid { position: absolute; inset: 0; opacity: .16; background-image: linear-gradient(rgba(255,255,255,.4) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.4) 1px,transparent 1px); background-size: 32px 32px; }
.upm-cover-orb { position: absolute; width: 240px; height: 240px; right: -45px; top: -145px; border-radius: 50%; background: rgba(255,255,255,.18); }
.upm-identity { position: relative; display: flex; align-items: center; min-height: 112px; padding: 24px 20px 18px 136px; }
.upm-avatar-wrap { position: absolute; left: 22px; top: -49px; width: 100px; height: 100px; padding: 4px; border-radius: 50%; background: white; box-shadow: 0 7px 20px rgba(24,48,82,.22); }
.upm-avatar { width: 92px !important; height: 92px !important; overflow: hidden; border-radius: 50% !important; object-fit: cover; }
.upm-avatar-fallback { display: grid; place-items: center; background: #dcecff; color: #0871db; font-size: 27px; font-weight: 700; }
.upm-avatar-wrap i { position: absolute; right: 5px; bottom: 8px; width: 15px; height: 15px; border: 3px solid white; border-radius: 50%; background: #adb6c4; }
.upm-avatar-wrap i.online { background: #23c16b; }
.upm-name { min-width: 0; flex: 1; }
.upm-name h1 { margin: 0 0 5px; overflow: hidden; font-size: 21px; text-overflow: ellipsis; white-space: nowrap; }
.upm-name small { display: block; margin: -1px 0 5px; color: #8994a8; font-size: 11px; }
.upm-name p { margin: 0; color: #768299; font-size: 13px; }
.upm-edit { padding: 7px 11px; border: 1px solid #b9d7f8; border-radius: 8px; background: #edf6ff; color: #0871db; cursor: pointer; font-weight: 600; }
.upm-actions { display: flex; gap: 8px; margin-left: 10px; }
.upm-nickname-edit { display: grid; flex: 0 0 32px; width: 32px; height: 32px; padding: 7px; place-items: center; border: 0; border-radius: 50%; background: #eef2f6; cursor: pointer; opacity: .72; transition: background .15s ease, opacity .15s ease; }
.upm-nickname-edit:hover { background: #dce8f6; opacity: 1; }
.upm-nickname-edit img { width: 15px; height: 15px; }
.upm-message { padding: 7px 12px; border: 1px solid #0871db; border-radius: 8px; background: #0871db; color: #fff; cursor: pointer; font-weight: 600; }
.upm-message:disabled { cursor: wait; opacity: .65; }
.upm-content { margin-top: 7px; padding: 20px 22px 24px; border-top: 8px solid #f4f6f9; }
.upm-content h3 { margin: 0 0 9px; font-size: 15px; }
.upm-content dl { margin: 0; }
.upm-content dl > div { display: grid; grid-template-columns: 112px minmax(0,1fr); gap: 12px; padding: 10px 0; }
.upm-content dt { color: #7e8a9e; }
.upm-content dd { margin: 0; color: #27344a; font-weight: 600; overflow-wrap: anywhere; }
.upm-divider { height: 1px; margin: 14px 0 18px; background: #edf1f5; }
.upm-status { display: inline-flex; padding: 3px 8px; border-radius: 99px; background: #e7f8ef; color: #15975a; font-size: 12px; }
.upm-loading { min-height: 420px; padding: 0 20px; }
.upm-loading div { height: 178px; margin: 0 -20px 28px; background: #e9eef5; }
.upm-loading span { display: block; width: 65%; height: 15px; margin: 13px 0; border-radius: 8px; background: #edf1f6; }
@media (max-width: 520px) { .upm-overlay { padding: 0; align-items: end; } .upm-dialog { width: 100%; max-height: 92vh; border-radius: 18px 18px 0 0; } .upm-cover { height: 150px; } }
</style>
